import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography
} from '@mui/material';

import { IPointAward } from 'components/awards/models';
import { QrCode } from 'components/awards/qr-code/QrCode';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { BackgroundBlur } from 'ui/BackgroundBlur';
import { DialogLiveClock, dialogLiveClockSlotProps } from 'ui/DialogLiveClock';

const PREFIX = 'AwardDetailsDialog';

const classes = {
    dialogContent: `${PREFIX}dialogContent`,
    colorTint: `${PREFIX}colorTint`
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
    [`& .${classes.dialogContent}`]: {
        color: theme.palette.text.primary
    },

    [`& .${classes.colorTint}`]: {
        color: theme.palette.hint
    }
}));

interface IProps {
    award: IPointAward;
    onClose: () => void;
}

export const AwardDetailsDialog: React.FC<IProps> = ({ onClose, award }) => {
    const { t } = useTranslation();

    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const [isTermsOpen, setIsTermsOpen] = React.useState<boolean>(false);

    const handleTermsAndConditionsClick = React.useCallback(() => {
        setIsTermsOpen(true);
    }, []);
    const handleTermsAndConditionsClose = React.useCallback(() => {
        setIsTermsOpen(false);
    }, []);

    const handleClose = React.useCallback(() => {
        onClose();
        handleTermsAndConditionsClose();
    }, [handleTermsAndConditionsClose, onClose]);

    const showQRCode = settings?.offlineCheckinEnabled;

    return (
        <>
            <BackgroundBlur open={!!award} />
            <StyledDialog onClose={handleClose} open={!!award} fullWidth slotProps={dialogLiveClockSlotProps}>
                {showQRCode && <DialogLiveClock />}
                {!isTermsOpen ? (
                    <>
                        <DialogTitle>
                            <Typography align="center">{award?.title}</Typography>
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText className={classes.dialogContent}>
                                {award?.visualisation.description}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleTermsAndConditionsClick} className={classes.colorTint}>
                                {t('AUTH_TERMS_TERMS_AND_CONDITIONS')}
                            </Button>
                        </DialogActions>
                        {showQRCode && <QrCode award={award} />}
                    </>
                ) : (
                    <>
                        <DialogTitle>
                            <Typography align="center">{t('AUTH_TERMS_TERMS_AND_CONDITIONS')}</Typography>
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText className={classes.dialogContent}>
                                {award?.terms}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleTermsAndConditionsClose} className={classes.colorTint}>
                                {t('AWARDS_TERMSANDCONDITIONS_ACTIONS_BACK')}
                            </Button>
                        </DialogActions>
                        {showQRCode && <QrCode award={award} />}
                    </>
                )}
            </StyledDialog>
        </>
    );
};
